import { transitionLabel } from '@/components/admin/sessions/walk/walk-labels';
import type { PageTransition } from '@matter_extractor/shared';

/**
 * The edge between two traversed pages (§12.2), carrying its transition -
 * « fondu · normal », the same reading as the scene of §9.1, because it is the
 * same object and it must read the same way.
 *
 * ## Why the transition is optional, and null everywhere today
 *
 * A transition lives on the page of the FROZEN experience (`experiencePageSchema`),
 * and `sessionDetailSchema` carries the session, its visits, its turns and the
 * cascade - protocol, persona, criteria - and no experience content. So nothing
 * this screen is served can say how a page gave way to the next one, and the
 * edge prints the bar alone rather than naming a transition nobody read: an
 * invented « fondu · normal » under every edge would be the one line of this
 * screen that is not a fact about the session.
 *
 * The prop exists so that the day the reading serves it, the edge already knows
 * how to say it - and so the sentence above has one place to be corrected.
 *
 * ## A note, where the edge is not a transition
 *
 * The determination of the noyau is not a page the session entered (noyau.md
 * §9.1, #152): the edge that leads to its block says HOW it came - « calculée en
 * fond, à la clôture du protocole » - where another edge would name a transition.
 */

export interface WalkEdgeProps {
  readonly transition: PageTransition | null;
  /** A sentence in place of a transition, for an edge that is not one. */
  readonly note?: string;
}

export function WalkEdge({ transition, note }: WalkEdgeProps) {
  return (
    <div className="flex items-center gap-2.5 py-1 pl-5 text-caption text-gray-500">
      {/* A BORDER and not a filled span: the charte keeps white as the ground,
          and `design-system-adherence.test.ts` refuses a grey fill on any
          surface - a two-pixel rule is drawn with a rule. */}
      <span aria-hidden="true" className="h-5 border-gray-200 border-l-2" />
      {transition === null ? null : <span>{transitionLabel(transition)}</span>}
      {transition === null && note !== undefined ? <span>{note}</span> : null}
    </div>
  );
}
